import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface ProjectGalleryProps {
  images: { url: string; caption: string }[];
  onClose?: () => void;
}

export const ProjectGallery: React.FC<ProjectGalleryProps> = ({
  images,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDirection(newDirection);
    setCurrentIndex(prevIndex => {
      let newIndex = prevIndex + newDirection;
      if (newIndex < 0) newIndex = images.length - 1;
      if (newIndex >= images.length) newIndex = 0;
      return newIndex;
    });
  };

  return (
    <div className="relative w-full aspect-video bg-gray-900 overflow-hidden rounded-lg">
      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 
            rounded-full text-white transition-colors z-50"
        >
          <X size={20} />
        </button>
      )}

      {/* Main Image */}
      <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.img
          key={currentIndex}
          src={images[currentIndex].url}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(_e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);
            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
          className="absolute w-full h-full object-contain"
          alt={images[currentIndex].caption}
        />
      </AnimatePresence>

      {/* Navigation Controls */}
      <div className="absolute inset-0 flex items-center justify-between z-30">
        <div className="p-4 h-full flex items-center">
          <button
            onClick={e => paginate(-1, e)}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors focus:outline-none"
          >
            <ChevronLeft className="text-white" size={24} />
          </button>
        </div>
        <div className="p-4 h-full flex items-center">
          <button
            onClick={e => paginate(1, e)}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors focus:outline-none"
          >
            <ChevronRight className="text-white" size={24} />
          </button>
        </div>
      </div>

      {/* Caption */}
      <div className="absolute bottom-12 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent z-20">
        <p className="text-white text-center">{images[currentIndex].caption}</p>
      </div>

      {/* Thumbnails */}
      <div className="absolute bottom-0 left-0 right-0 p-4 flex justify-center gap-2 z-20">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentIndex ? 'bg-white' : 'bg-white/50'
            } hover:bg-white focus:outline-none`}
          />
        ))}
      </div>
    </div>
  );
};
